import Honeybadger from 'honeybadger-js';
import { ApolloClient } from 'apollo-boost';
import { AxiosResponse } from 'axios';
import { camelCase, mapKeys } from 'lodash';

import { MediaApiJwtQuery, MediaApiJwtQueryDocument } from '../../generated/graphql';

type ErrorTypes = 'TOO_SMALL' | 'UNSUPPORTED_FILE' | 'UPLOAD_FAILED';
export type ImageError = { type: ErrorTypes; message: string };
export interface ImageResult {
  autoRotate?: boolean;
  checkSum?: string;
  creation?: string;
  deleted?: boolean;
  deletedDate?: string;
  description: string;
  focalPoint?: any[];
  height: number;
  hotSpots?: any[];
  id: string;
  imageType?: string;
  mediaApiUrl: string;
  mediaApiId: string;
  migrated?: boolean;
  ownerIds?: string[];
  styleGroup?: any;
  tags?: any[];
  width: number;
}
export interface ImageMetadata {
  date?: string;
  caption?: string;
  aspectRatio?: { name: string; value: number } | null | undefined;
  height?: number;
  width?: number;
  x?: number;
  y?: number;
  rotation?: number;
}

export type MediaApiResponse = AxiosResponse<ImageResult | undefined> & { error?: ImageError };

export const getMediaApiId = (url?: string) => {
  const match = url && url.match(/\/([a-zA-Z0-9-_]+)([?~].+)?$/);

  if (match) {
    return match[1];
  }
};

export const extension = (file: File | Blob) => file.type.split('/').pop();

export const mediaApiUrl = () => process.env.REACT_APP_MEDIA_API_URL || '';

export const camelCaseKeys = (obj: any): any => mapKeys(obj, (_v, k) => camelCase(k));

export const getMediaApiJwt = async (apolloClient: ApolloClient<object>) => {
  const res = await apolloClient.query<MediaApiJwtQuery>({
    query: MediaApiJwtQueryDocument,
    fetchPolicy: 'no-cache',
  });
  const { data } = res;
  return data.mediaApiJwt?.token;
};

export const genericErrorResponse = {
  status: 500,
  statusText: 'Network Error',
  data: undefined,
  headers: undefined,
  config: {},
};

export const handleMediaAPIResponse = (res: MediaApiResponse): MediaApiResponse => {
  const apiUrl = mediaApiUrl();
  if (res && res.data && res.status === 200) {
    const { height, width, id, ...rest } = res.data;
    const mediaApiUrl = `${apiUrl}/images/${id}`;
    return {
      ...res,
      data: {
        mediaApiUrl,
        mediaApiId: id,
        height: parseInt(String(height)),
        width: parseInt(String(width)),
        ...camelCaseKeys(rest),
      },
    };
  } else if (res.status === 400) {
    return {
      ...res,
      error: { type: 'UNSUPPORTED_FILE', message: 'Unsupported file type.' },
    };
  } else {
    return {
      ...res,
      error: {
        type: 'UPLOAD_FAILED',
        message: "Oops! We couldn't upload your photo. Try again in a few minutes.",
      },
    };
  }
};

export const handleMediaAPIException = (error: Error): MediaApiResponse => {
  Honeybadger.notify(error, 'Exception uploading to Media API');
  return {
    data: undefined,
    status: 500,
    statusText: 'Exception uploading to Media API',
    headers: {},
    config: {},
    error: { type: 'UPLOAD_FAILED', message: "Oops! We couldn't upload your photo. Try again in a few minutes." },
  };
};
